import { Fragment, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import styles from '../shared/mhlShared.module.scss'
import localStyles from './MilitaryHearingLossWebsiteVisitors.module.scss'
import { Pill } from '../shared/SourceTabs'
import type { ChaseMessage } from '../shared/mhlTypes'
import { formatDate, formatDateTime } from '../shared/mhlFormatting'
import { MHL_PAGE_SIZE, TablePagination, usePagedRows } from '../shared/TablePagination'

interface Props {
    messages: ChaseMessage[]
    loading?: boolean
    emptyMessage?: string
    pageSize?: number
    /** Hide the client / case column (already in context on the client record). */
    hideClient?: boolean
    /** Optional name lookup so the table can show who a message went to. */
    clientNames?: Record<string, string>
}

const looksLikeHtml = (value: string | null | undefined) => !!value && /<\/?[a-z][\s\S]*>/i.test(value)

/**
 * Every chase email / SMS logged in communications_history for the MHL
 * document chase, with the message body available on demand.
 */
export const ChaseMessagesTable = ({
    messages,
    loading = false,
    emptyMessage = 'No chase messages have been sent yet.',
    pageSize = MHL_PAGE_SIZE,
    hideClient = false,
    clientNames,
}: Props) => {
    const navigate = useNavigate()
    const { rows, page, totalPages, setPage, pageSize: size } = usePagedRows(messages, pageSize)
    const [openId, setOpenId] = useState<number | null>(null)

    const colCount = hideClient ? 4 : 5

    return (
        <div className={styles.tableWrap}>
            <table className={`${styles.table} ${localStyles.messagesTable}`}>
                <thead>
                    <tr>
                        <th>Sent</th>
                        <th>Type</th>
                        {!hideClient && <th>Client</th>}
                        <th>Recipient</th>
                        <th>Message</th>
                    </tr>
                </thead>
                <tbody>
                    {loading ? (
                        <tr>
                            <td colSpan={colCount} className={styles.emptyState}>
                                Loading…
                            </td>
                        </tr>
                    ) : rows.length === 0 ? (
                        <tr>
                            <td colSpan={colCount} className={styles.emptyState}>
                                {emptyMessage}
                            </td>
                        </tr>
                    ) : (
                        rows.map((m) => {
                            const isOpen = openId === m.id
                            const isSms = m.com_type === 'sms'
                            const name = clientNames?.[m.case_ref]
                            return (
                                <Fragment key={m.id}>
                                    <tr className={isOpen ? localStyles.rowExpanded : ''}>
                                        <td>
                                            <span className={styles.stackSecondary}>
                                                {m.sent_at ? formatDateTime(m.sent_at) : formatDate(m.send_day || m.send_date)}
                                            </span>
                                        </td>
                                        <td>
                                            <Pill tone={isSms ? 'info' : 'brand'}>{isSms ? 'SMS' : 'Email'}</Pill>
                                        </td>
                                        {!hideClient && (
                                            <td>
                                                <div className={styles.stackCell}>
                                                    {m.client_id ? (
                                                        <button
                                                            type="button"
                                                            className={localStyles.messageToggle}
                                                            onClick={() => navigate(`/military-hearing-loss/client/${m.client_id}`)}
                                                        >
                                                            {name || `Client #${m.client_id}`}
                                                        </button>
                                                    ) : (
                                                        <span className={styles.stackSecondary}>{name || '—'}</span>
                                                    )}
                                                    <span className={`${styles.stackSecondary} ${localStyles.mono}`}>{m.case_ref}</span>
                                                </div>
                                            </td>
                                        )}
                                        <td>
                                            <span className={styles.stackSecondary}>{m.recipient_address || '—'}</span>
                                        </td>
                                        <td>
                                            <div className={styles.stackCell}>
                                                <span className={styles.stackPrimary} style={{ fontSize: 13 }}>
                                                    {m.com_title || (isSms ? 'SMS' : 'Email')}
                                                </span>
                                                {m.com_message && (
                                                    <button
                                                        type="button"
                                                        className={localStyles.messageToggle}
                                                        onClick={() => setOpenId(isOpen ? null : m.id)}
                                                    >
                                                        {isOpen ? 'Hide message' : 'Show message'}
                                                    </button>
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                    {isOpen && m.com_message && (
                                        <tr>
                                            <td colSpan={colCount}>
                                                {looksLikeHtml(m.com_message) ? (
                                                    <div
                                                        className={`${localStyles.messageBody} ${localStyles.emailPreview}`}
                                                        // Message bodies are our own outbound templates from communications_history.
                                                        dangerouslySetInnerHTML={{ __html: m.com_message }}
                                                    />
                                                ) : (
                                                    <div className={localStyles.messageBody}>{m.com_message}</div>
                                                )}
                                            </td>
                                        </tr>
                                    )}
                                </Fragment>
                            )
                        })
                    )}
                </tbody>
            </table>

            <TablePagination page={page} totalPages={totalPages} total={messages.length} pageSize={size} onChange={setPage} />
        </div>
    )
}
